import type { Activity } from '@cognite/apm-client';
import { EmptyState, Skeleton } from '@cognite/cogs-core';
import {
  ErrorCodePlaceholderIllustration,
  PlansIllustration,
} from '@cognite/cogs-icons';
import { LOCIZE_NAMESPACES, useTranslation } from '@infield/features/i18n';
import { EmptyStateWrapper } from '@infield/features/ui/table-empty-state/elements';
import type { FC } from 'react';

import { NUMBER_OF_ACTIVITIES_TO_FIT_CARD } from './asset-activities-card';
import { AssetActivitiesList } from './asset-activities-list';

type Props = {
  isInitialLoading: boolean;
  isError: boolean;
  filteredActivities?: Activity[];
  onActivityClick: (activityExternalId: string) => void;
};

export const AssetActivitiesCardContent: FC<Props> = ({
  isInitialLoading,
  isError,
  filteredActivities,
  onActivityClick,
}) => {
  const { t } = useTranslation(LOCIZE_NAMESPACES.activity);

  const hasFilteredActivities =
    filteredActivities && filteredActivities.length > 0;

  if (isError) {
    return (
      <EmptyStateWrapper>
        <EmptyState
          illustration={<ErrorCodePlaceholderIllustration statusCode={400} />}
          title={t('ASSET_OVERVIEW_ACTIVITIES_CARD_ERROR_TITLE', 'Data error')}
          description={t(
            'ASSET_OVERVIEW_ACTIVITIES_CARD_ERROR_BODY',
            "Something went wrong, and the work orders aren't loading. Try again or contact support."
          )}
        />
      </EmptyStateWrapper>
    );
  }

  if (isInitialLoading) {
    return <Skeleton.List lines={NUMBER_OF_ACTIVITIES_TO_FIT_CARD} />;
  }

  if (!hasFilteredActivities) {
    return (
      <EmptyStateWrapper>
        <EmptyState
          illustration={<PlansIllustration />}
          title={t(
            'ASSET_OVERVIEW_ACTIVITIES_CARD_NO_ACTIVITY_TITLE',
            'No work orders'
          )}
          description={t(
            'ASSET_OVERVIEW_ACTIVITIES_CARD_NO_ACTIVITY_BODY',
            'There are no work orders found on this asset.'
          )}
        />
      </EmptyStateWrapper>
    );
  }

  return (
    <AssetActivitiesList
      activities={filteredActivities}
      onActivityClick={onActivityClick}
    />
  );
};
